<!--
  @copyright 汽车商贸管理平台 V1.0
  @year 2026
  @description Dashboard
-->
<template>
  <div class="dashboard">
    <van-nav-bar title="数据看板" left-arrow @click-left="$router.back()" />

    <!-- 时间筛选 -->
    <div class="filter-bar">
      <van-dropdown-menu>
        <van-dropdown-item v-model="dateRange" :options="dateOptions" @change="loadData" />
      </van-dropdown-menu>
    </div>

    <!-- 总览卡片 -->
    <div class="stats-grid">
      <div class="stat-card">
        <div class="stat-value">{{ basicData.contractCount || 0 }}</div>
        <div class="stat-label">合同签订量</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">{{ basicData.salesCount || 0 }}</div>
        <div class="stat-label">销售辆数</div>
      </div>
      <div class="stat-card highlight">
        <div class="stat-value">{{ basicData.contractRevenue || 0 }}</div>
        <div class="stat-label">合同营收(万元)</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">{{ basicData.grossMargin || 0 }}</div>
        <div class="stat-label">毛利(万元)</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">{{ basicData.advancePayment || 0 }}</div>
        <div class="stat-label">垫资金额(万元)</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">{{ basicData.invoiceAmount || 0 }}</div>
        <div class="stat-label">开票金额(万元)</div>
      </div>
    </div>

    <!-- 渠道统计 -->
    <div class="section" v-if="channelData.length > 0">
      <div class="section-title">渠道统计</div>
      <div class="channel-list">
        <div class="channel-item" v-for="item in channelData" :key="item.channel">
          <div class="channel-name">{{ item.channel || '未知' }}</div>
          <div class="channel-stats">
            <div class="stat-item">
              <span class="stat-num">{{ item.contractCount }}</span>
              <span class="stat-desc">合同数</span>
            </div>
            <div class="stat-item">
              <span class="stat-num">{{ item.salesCount }}</span>
              <span class="stat-desc">销售量</span>
            </div>
            <div class="stat-item">
              <span class="stat-num">{{ item.contractAmount }}</span>
              <span class="stat-desc">金额(万)</span>
            </div>
            <div class="stat-item">
              <span class="stat-num">{{ item.grossMargin }}</span>
              <span class="stat-desc">毛利(万)</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 国家分布 -->
    <div class="section" v-if="countryData.length > 0">
      <div class="section-title">出口国家分布</div>
      <div class="country-list">
        <div class="country-item" v-for="item in countryData" :key="item.country">
          <span class="country-name">{{ item.country || '未知' }}</span>
          <span class="country-count">{{ item.orderCount }} 单</span>
          <span class="country-rate">{{ item.rate }}</span>
        </div>
      </div>
    </div>

    <div v-if="loading" class="loading-tip">加载中...</div>
  </div>
</template>

<script>
import http from "../utils/http";

export default {
  name: "Dashboard",
  data() {
    return {
      dateRange: 0,
      dateOptions: [
        { text: "本月", value: 0 },
        { text: "上月", value: 1 },
        { text: "本季度", value: 2 },
        { text: "本年", value: 3 },
      ],
      basicData: {},
      channelData: [],
      countryData: [],
      loading: false,
    };
  },
  created() {
    this.loadData();
  },
  methods: {
    getDateRange() {
      const now = new Date();
      let begin, end;
      switch (this.dateRange) {
        case 0: // 本月
          begin = new Date(now.getFullYear(), now.getMonth(), 1);
          end = now;
          break;
        case 1: // 上月
          begin = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          end = new Date(now.getFullYear(), now.getMonth(), 0);
          break;
        case 2: // 本季度
          const quarter = Math.floor(now.getMonth() / 3);
          begin = new Date(now.getFullYear(), quarter * 3, 1);
          end = now;
          break;
        case 3: // 本年
          begin = new Date(now.getFullYear(), 0, 1);
          end = now;
          break;
        default:
          begin = new Date(now.getFullYear(), now.getMonth(), 1);
          end = now;
      }
      return {
        begin: begin.toISOString().split("T")[0],
        end: end.toISOString().split("T")[0],
      };
    },
    async loadData() {
      this.loading = true;
      try {
        const { begin, end } = this.getDateRange();
        const res = await http.get(
          `/api/Dashboard/GetDashboardData?beginDate=${begin}&endDate=${end}`
        );
        if (res.code === 0 && res.data) {
          this.basicData = res.data.basicData || {};
          this.channelData = res.data.channelData || [];
          this.countryData = res.data.countryData || [];
        }
      } catch (error) {
        console.error("加载看板数据失败:", error);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.dashboard {
  max-width: 512px;
  margin: 0 auto;
  min-height: 100vh;
  background-color: #f5f5f5;
}

.filter-bar {
  background: #fff;
  margin-bottom: 8px;
}

.stats-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  padding: 12px;
}

.stat-card {
  background: #fff;
  border-radius: 8px;
  padding: 12px;
  text-align: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
}

.stat-card.highlight {
  background: linear-gradient(135deg, #007aff, #5856d6);
  color: #fff;
}

.stat-card.highlight .stat-value,
.stat-card.highlight .stat-label {
  color: #fff;
}

.stat-value {
  font-size: 22px;
  font-weight: bold;
  color: #333;
}

.stat-label {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
}

.section {
  background: #fff;
  margin: 0 12px 12px;
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
}

.section-title {
  font-size: 15px;
  font-weight: bold;
  color: #333;
  margin-bottom: 10px;
}

.channel-item {
  padding: 10px 0;
  border-bottom: 1px solid #f5f5f5;
}

.channel-item:last-child {
  border-bottom: none;
}

.channel-name {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 8px;
}

.channel-stats {
  display: flex;
  justify-content: space-between;
}

.stat-item {
  text-align: center;
}

.stat-num {
  display: block;
  font-size: 16px;
  font-weight: bold;
  color: #007aff;
}

.stat-desc {
  display: block;
  font-size: 11px;
  color: #999;
  margin-top: 2px;
}

.country-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f5f5f5;
}

.country-item:last-child {
  border-bottom: none;
}

.country-name {
  font-size: 14px;
  color: #333;
}

.country-count {
  font-size: 13px;
  color: #666;
}

.country-rate {
  font-size: 13px;
  color: #007aff;
  font-weight: 500;
}

.loading-tip {
  text-align: center;
  color: #999;
  padding: 20px;
}
</style>
